"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { Clock3, MapPin } from "lucide-react";
import { Button } from "@/app/components/ui/button";
import { ContactFormVariant } from "@/app/components/forms/contact/contact-form-variant";
import type { JobDetailT } from "@/app/types/jobs";

export type CareerJobLocationT = {
  readonly id: string;
  readonly name: string;
  readonly location_name: string | null;
  readonly company_name: string;
  readonly address: string;
  readonly zip_code: string;
  readonly city: string;
  readonly phone: string;
  readonly website_url: string | null;
};

export type CareerJobContactPersonT = {
  readonly id: string;
  readonly name: string;
  readonly title: string | null;
  readonly image_url: string | null;
};

type CareerJobDetailClientPropsT = {
  readonly job: JobDetailT;
  readonly location: CareerJobLocationT | null;
  readonly contactPerson: CareerJobContactPersonT | null;
};

const formatLocationBadge = (
  location: CareerJobLocationT | null
): string | null => (location ? `${location.city}` : null);

const toDescriptionParagraphs = (value: string): readonly string[] =>
  value
    .split(/\r?\n\r?\n|\r?\n/g)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

export default function CareerJobDetailClient({
  job,
  location,
  contactPerson,
}: CareerJobDetailClientPropsT) {
  const [isApplicationVisible, setIsApplicationVisible] = useState(false);
  const jobIntroRef = useRef<HTMLDivElement>(null);
  const isInitialMountRef = useRef(true);

  const taskParagraphs = toDescriptionParagraphs(job.job_tasks);
  const requirementItems = job.job_requirements;
  const benefitItems = job.benefits;
  const locationBadge = formatLocationBadge(location);
  const employmentLabel = job.employment_type;

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    const frameId = requestAnimationFrame(() => {
      jobIntroRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });

    return () => cancelAnimationFrame(frameId);
  }, [isApplicationVisible]);

  return (
    <article className="bg-background border-border overflow-hidden rounded-lg border">
      <div className="bg-muted relative aspect-[16/9] w-full overflow-hidden rounded-t-lg md:aspect-[16/9]">
        {job.thumbnail_image?.url ? (
          <Image
            src={job.thumbnail_image.url}
            alt={job.thumbnail_image.alt_text || job.title}
            fill
            priority
            className="object-cover"
            sizes="(max-width: 1024px) 100vw, 960px"
          />
        ) : (
          <div className="from-muted to-muted/80 text-muted-foreground flex h-full items-center justify-center bg-linear-to-br text-sm">
            Kein Vorschaubild hinterlegt
          </div>
        )}
      </div>

      <div className="px-6 py-6 md:px-8 md:py-8">
        <div
          ref={jobIntroRef}
          id="job-intro"
          className="scroll-mt-24 space-y-3"
        >
          <p className="text-muted-foreground text-sm font-semibold">
            Wir suchen Sie!
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="heading-medium text-foreground font-semibold">
              {job.title} (m/w/d)
            </span>
            <span className="bg-light-blue inline-flex items-center gap-2 rounded-sm px-3 py-1 text-xs font-medium text-gray-700">
              <Clock3 className="size-3.5" />
              {employmentLabel}
            </span>
            {locationBadge ? (
              <span className="bg-light-blue inline-flex items-center gap-2 rounded-sm px-3 py-1 text-xs font-medium text-gray-700">
                <MapPin className="size-3.5" />
                {locationBadge}
              </span>
            ) : null}
          </div>
        </div>

        <div className="border-border mt-8 border-t pt-8">
          {isApplicationVisible ? (
            <ContactFormVariant
              variant="job"
              showHeader={false}
              presentation="plain"
              variantContext={{
                jobTitle: job.title,
                jobSlug: job.slug,
                employmentType: job.employment_type,
                employmentLabel,
                locationName:
                  (location?.location_name ||
                    location?.name ||
                    location?.city) ??
                  null,
                locationId: location?.id ?? null,
              }}
              renderFooter={({ submitButton }) => (
                <div className="border-border flex flex-col gap-4 border-t pt-8 sm:flex-row sm:items-center sm:justify-between">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setIsApplicationVisible(false)}
                  >
                    Zurück
                  </Button>
                  {submitButton}
                </div>
              )}
            />
          ) : (
            <div className="space-y-10">
              <section className="space-y-4">
                <h2 className="heading-small">Das sind Ihre Aufgaben:</h2>
                <div className="text-muted-foreground space-y-1 text-sm leading-7">
                  {taskParagraphs.length > 0 ? (
                    taskParagraphs.map((paragraph, index) => (
                      <p key={`${job.id}-task-${index}`}>{paragraph}</p>
                    ))
                  ) : (
                    <p>
                      Zu dieser Stelle wurden noch keine Aufgaben hinterlegt.
                    </p>
                  )}
                </div>
              </section>

              <div className="grid gap-10 md:grid-cols-2">
                <section className="space-y-4">
                  <h2 className="heading-small">Das bringen Sie mit:</h2>
                  <ul className="text-muted-foreground space-y-1 text-sm leading-7">
                    {requirementItems.length > 0 ? (
                      requirementItems.map((item) => (
                        <li key={item} className="ml-5 list-disc pl-1">
                          {item}
                        </li>
                      ))
                    ) : (
                      <li className="ml-5 list-disc pl-1">
                        Anforderungen werden in Kürze ergänzt.
                      </li>
                    )}
                  </ul>
                </section>

                <section className="space-y-4">
                  <h2 className="heading-small">Das erwartet Sie:</h2>
                  <ul className="text-muted-foreground space-y-1 text-sm leading-7">
                    {benefitItems.length > 0 ? (
                      benefitItems.map((item) => (
                        <li key={item} className="ml-5 list-disc pl-1">
                          {item}
                        </li>
                      ))
                    ) : (
                      <li className="ml-5 list-disc pl-1">
                        Benefits werden in Kürze ergänzt.
                      </li>
                    )}
                  </ul>
                </section>
              </div>

              <p className="text-muted-foreground text-sm leading-7">
                Wenn Sie diese berufliche Herausforderung mit interessanten
                Perspektiven reizt, dann möchten wir Sie kennenlernen und freuen
                uns auf Ihre Online-Bewerbung!
              </p>

              <div>
                <Button onClick={() => setIsApplicationVisible(true)}>
                  Jetzt bewerben
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {(location || contactPerson) && (
        <footer className="border-border grid gap-8 border-t px-6 pt-14 pb-6 md:grid-cols-2 md:px-8">
          <div className="space-y-1 text-sm leading-6">
            {location ? (
              <>
                <p className="text-foreground font-semibold">
                  {location.company_name}
                </p>
                <p className="text-muted-foreground">
                  {location.address}, {location.zip_code} {location.city}
                </p>
                <p className="text-muted-foreground">Tel.: {location.phone}</p>
              </>
            ) : null}
          </div>

          {contactPerson ? (
            <div className="justify-self-start md:justify-self-end">
              <p className="text-foreground mb-3 text-sm font-semibold">
                Ansprechpartner:
              </p>
              <div className="flex items-center gap-3">
                <div className="bg-muted relative h-14 w-14 overflow-hidden rounded-full">
                  {contactPerson.image_url ? (
                    <Image
                      src={contactPerson.image_url}
                      alt={contactPerson.name}
                      fill
                      className="object-cover"
                      sizes="56px"
                    />
                  ) : null}
                </div>
                <div className="text-muted-foreground text-sm leading-5">
                  <p className="text-foreground font-semibold">
                    {contactPerson.name}
                  </p>
                  {contactPerson.title ? (
                    <p className="text-muted-foreground">
                      {contactPerson.title}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </footer>
      )}
    </article>
  );
}
